/**
 * Generated by orval v7.10.0 🍺
 * Do not edit manually.
 * Fintecy Investors API
 * API for managing investors, accounts, and transactions in the Fintecy platform
 * OpenAPI spec version: 1.0.0
 */
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useMutation, useQuery } from '@tanstack/react-query';

import type {
  Error,
  GetPlaidItemTransactionsParams,
  PlaidImportRequest,
  PlaidImportResult,
  PlaidInstitutionListResponse,
  PlaidItem,
  PlaidItemInput,
  PlaidItemListResponse,
  PlaidLinkTokenResponse,
  PlaidLinkedAccountListResponse,
  PlaidStagedTransactionListResponse,
  PlaidSyncResult,
  SearchPlaidInstitutionsParams,
} from '.././model';

import { customInstance } from '../../axios-instance';

/**
 * Searches the locally cached Plaid institution catalog. Answers from the database, so it keeps working when Plaid is unreachable or unconfigured; entries are refreshed from Plaid in the background when they are missing or stale.
 * @summary Search cached Plaid institutions
 */
export const searchPlaidInstitutions = (
  params?: SearchPlaidInstitutionsParams,
  signal?: AbortSignal
) => {
  return customInstance<PlaidInstitutionListResponse>({
    url: `/api/integrations/plaid/institutions`,
    method: 'GET',
    params,
    signal,
  });
};

export const getSearchPlaidInstitutionsQueryKey = (params?: SearchPlaidInstitutionsParams) => {
  return [`/api/integrations/plaid/institutions`, ...(params ? [params] : [])] as const;
};

export const getSearchPlaidInstitutionsQueryOptions = <
  TData = Awaited<ReturnType<typeof searchPlaidInstitutions>>,
  TError = Error,
>(
  params?: SearchPlaidInstitutionsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof searchPlaidInstitutions>>, TError, TData>
    >;
  }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getSearchPlaidInstitutionsQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof searchPlaidInstitutions>>> = ({
    signal,
  }) => searchPlaidInstitutions(params, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof searchPlaidInstitutions>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type SearchPlaidInstitutionsQueryResult = NonNullable<
  Awaited<ReturnType<typeof searchPlaidInstitutions>>
>;
export type SearchPlaidInstitutionsQueryError = Error;

export function useSearchPlaidInstitutions<
  TData = Awaited<ReturnType<typeof searchPlaidInstitutions>>,
  TError = Error,
>(
  params: undefined | SearchPlaidInstitutionsParams,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof searchPlaidInstitutions>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof searchPlaidInstitutions>>,
          TError,
          Awaited<ReturnType<typeof searchPlaidInstitutions>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useSearchPlaidInstitutions<
  TData = Awaited<ReturnType<typeof searchPlaidInstitutions>>,
  TError = Error,
>(
  params?: SearchPlaidInstitutionsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof searchPlaidInstitutions>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof searchPlaidInstitutions>>,
          TError,
          Awaited<ReturnType<typeof searchPlaidInstitutions>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useSearchPlaidInstitutions<
  TData = Awaited<ReturnType<typeof searchPlaidInstitutions>>,
  TError = Error,
>(
  params?: SearchPlaidInstitutionsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof searchPlaidInstitutions>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Search cached Plaid institutions
 */

export function useSearchPlaidInstitutions<
  TData = Awaited<ReturnType<typeof searchPlaidInstitutions>>,
  TError = Error,
>(
  params?: SearchPlaidInstitutionsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof searchPlaidInstitutions>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getSearchPlaidInstitutionsQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Mints the short-lived token the browser opens Plaid Link with.
 * @summary Create a Plaid Link token
 */
export const createPlaidLinkToken = (signal?: AbortSignal) => {
  return customInstance<PlaidLinkTokenResponse>({
    url: `/api/integrations/plaid/link-token`,
    method: 'POST',
    signal,
  });
};

export const getCreatePlaidLinkTokenMutationOptions = <
  TError = Error,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createPlaidLinkToken>>,
    TError,
    void,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createPlaidLinkToken>>,
  TError,
  void,
  TContext
> => {
  const mutationKey = ['createPlaidLinkToken'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createPlaidLinkToken>>,
    void
  > = () => {
    return createPlaidLinkToken();
  };

  return { mutationFn, ...mutationOptions };
};

export type CreatePlaidLinkTokenMutationResult = NonNullable<
  Awaited<ReturnType<typeof createPlaidLinkToken>>
>;

export type CreatePlaidLinkTokenMutationError = Error;

/**
 * @summary Create a Plaid Link token
 */
export const useCreatePlaidLinkToken = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createPlaidLinkToken>>,
      TError,
      void,
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<Awaited<ReturnType<typeof createPlaidLinkToken>>, TError, void, TContext> => {
  const mutationOptions = getCreatePlaidLinkTokenMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * @summary List linked Plaid items
 */
export const getPlaidItems = (signal?: AbortSignal) => {
  return customInstance<PlaidItemListResponse>({
    url: `/api/integrations/plaid/items`,
    method: 'GET',
    signal,
  });
};

export const getGetPlaidItemsQueryKey = () => {
  return [`/api/integrations/plaid/items`] as const;
};

export const getGetPlaidItemsQueryOptions = <
  TData = Awaited<ReturnType<typeof getPlaidItems>>,
  TError = Error,
>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getPlaidItems>>, TError, TData>>;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetPlaidItemsQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getPlaidItems>>> = ({ signal }) =>
    getPlaidItems(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getPlaidItems>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetPlaidItemsQueryResult = NonNullable<Awaited<ReturnType<typeof getPlaidItems>>>;
export type GetPlaidItemsQueryError = Error;

export function useGetPlaidItems<TData = Awaited<ReturnType<typeof getPlaidItems>>, TError = Error>(
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getPlaidItems>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getPlaidItems>>,
          TError,
          Awaited<ReturnType<typeof getPlaidItems>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetPlaidItems<TData = Awaited<ReturnType<typeof getPlaidItems>>, TError = Error>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getPlaidItems>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getPlaidItems>>,
          TError,
          Awaited<ReturnType<typeof getPlaidItems>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetPlaidItems<TData = Awaited<ReturnType<typeof getPlaidItems>>, TError = Error>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getPlaidItems>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List linked Plaid items
 */

export function useGetPlaidItems<TData = Awaited<ReturnType<typeof getPlaidItems>>, TError = Error>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getPlaidItems>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetPlaidItemsQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Exchanges the Link public token for an access token server side, stores it encrypted, and records the item. The access token is never returned to the client.
 * @summary Exchange a Plaid public token
 */
export const createPlaidItem = (plaidItemInput: PlaidItemInput, signal?: AbortSignal) => {
  return customInstance<PlaidItem>({
    url: `/api/integrations/plaid/items`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: plaidItemInput,
    signal,
  });
};

export const getCreatePlaidItemMutationOptions = <TError = Error, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createPlaidItem>>,
    TError,
    { data: PlaidItemInput },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createPlaidItem>>,
  TError,
  { data: PlaidItemInput },
  TContext
> => {
  const mutationKey = ['createPlaidItem'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createPlaidItem>>,
    { data: PlaidItemInput }
  > = (props) => {
    const { data } = props ?? {};

    return createPlaidItem(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreatePlaidItemMutationResult = NonNullable<
  Awaited<ReturnType<typeof createPlaidItem>>
>;
export type CreatePlaidItemMutationBody = PlaidItemInput;
export type CreatePlaidItemMutationError = Error;

/**
 * @summary Exchange a Plaid public token
 */
export const useCreatePlaidItem = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createPlaidItem>>,
      TError,
      { data: PlaidItemInput },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof createPlaidItem>>,
  TError,
  { data: PlaidItemInput },
  TContext
> => {
  const mutationOptions = getCreatePlaidItemMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Fetches accounts and the incremental transaction changes since the stored cursor, and persists both. Staged data only; nothing reaches the user's accounts until imported.
 * @summary Pull accounts and transactions for an item
 */
export const syncPlaidItem = (itemId: string, signal?: AbortSignal) => {
  return customInstance<PlaidSyncResult>({
    url: `/api/integrations/plaid/items/${itemId}/sync`,
    method: 'POST',
    signal,
  });
};

export const getSyncPlaidItemMutationOptions = <TError = Error, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof syncPlaidItem>>,
    TError,
    { itemId: string },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof syncPlaidItem>>,
  TError,
  { itemId: string },
  TContext
> => {
  const mutationKey = ['syncPlaidItem'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof syncPlaidItem>>,
    { itemId: string }
  > = (props) => {
    const { itemId } = props ?? {};

    return syncPlaidItem(itemId);
  };

  return { mutationFn, ...mutationOptions };
};

export type SyncPlaidItemMutationResult = NonNullable<Awaited<ReturnType<typeof syncPlaidItem>>>;

export type SyncPlaidItemMutationError = Error;

/**
 * @summary Pull accounts and transactions for an item
 */
export const useSyncPlaidItem = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof syncPlaidItem>>,
      TError,
      { itemId: string },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof syncPlaidItem>>,
  TError,
  { itemId: string },
  TContext
> => {
  const mutationOptions = getSyncPlaidItemMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * @summary List staged accounts for an item
 */
export const getPlaidItemAccounts = (itemId: string, signal?: AbortSignal) => {
  return customInstance<PlaidLinkedAccountListResponse>({
    url: `/api/integrations/plaid/items/${itemId}/accounts`,
    method: 'GET',
    signal,
  });
};

export const getGetPlaidItemAccountsQueryKey = (itemId: string) => {
  return [`/api/integrations/plaid/items/${itemId}/accounts`] as const;
};

export const getGetPlaidItemAccountsQueryOptions = <
  TData = Awaited<ReturnType<typeof getPlaidItemAccounts>>,
  TError = Error,
>(
  itemId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getPlaidItemAccounts>>, TError, TData>
    >;
  }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetPlaidItemAccountsQueryKey(itemId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getPlaidItemAccounts>>> = ({ signal }) =>
    getPlaidItemAccounts(itemId, signal);

  return { queryKey, queryFn, enabled: !!itemId, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getPlaidItemAccounts>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetPlaidItemAccountsQueryResult = NonNullable<
  Awaited<ReturnType<typeof getPlaidItemAccounts>>
>;
export type GetPlaidItemAccountsQueryError = Error;

export function useGetPlaidItemAccounts<
  TData = Awaited<ReturnType<typeof getPlaidItemAccounts>>,
  TError = Error,
>(
  itemId: string,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getPlaidItemAccounts>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getPlaidItemAccounts>>,
          TError,
          Awaited<ReturnType<typeof getPlaidItemAccounts>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetPlaidItemAccounts<
  TData = Awaited<ReturnType<typeof getPlaidItemAccounts>>,
  TError = Error,
>(
  itemId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getPlaidItemAccounts>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getPlaidItemAccounts>>,
          TError,
          Awaited<ReturnType<typeof getPlaidItemAccounts>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetPlaidItemAccounts<
  TData = Awaited<ReturnType<typeof getPlaidItemAccounts>>,
  TError = Error,
>(
  itemId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getPlaidItemAccounts>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List staged accounts for an item
 */

export function useGetPlaidItemAccounts<
  TData = Awaited<ReturnType<typeof getPlaidItemAccounts>>,
  TError = Error,
>(
  itemId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getPlaidItemAccounts>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetPlaidItemAccountsQueryOptions(itemId, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * @summary List staged transactions for an item
 */
export const getPlaidItemTransactions = (
  itemId: string,
  params?: GetPlaidItemTransactionsParams,
  signal?: AbortSignal
) => {
  return customInstance<PlaidStagedTransactionListResponse>({
    url: `/api/integrations/plaid/items/${itemId}/transactions`,
    method: 'GET',
    params,
    signal,
  });
};

export const getGetPlaidItemTransactionsQueryKey = (
  itemId: string,
  params?: GetPlaidItemTransactionsParams
) => {
  return [
    `/api/integrations/plaid/items/${itemId}/transactions`,
    ...(params ? [params] : []),
  ] as const;
};

export const getGetPlaidItemTransactionsQueryOptions = <
  TData = Awaited<ReturnType<typeof getPlaidItemTransactions>>,
  TError = Error,
>(
  itemId: string,
  params?: GetPlaidItemTransactionsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getPlaidItemTransactions>>, TError, TData>
    >;
  }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetPlaidItemTransactionsQueryKey(itemId, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getPlaidItemTransactions>>> = ({
    signal,
  }) => getPlaidItemTransactions(itemId, params, signal);

  return { queryKey, queryFn, enabled: !!itemId, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getPlaidItemTransactions>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetPlaidItemTransactionsQueryResult = NonNullable<
  Awaited<ReturnType<typeof getPlaidItemTransactions>>
>;
export type GetPlaidItemTransactionsQueryError = Error;

export function useGetPlaidItemTransactions<
  TData = Awaited<ReturnType<typeof getPlaidItemTransactions>>,
  TError = Error,
>(
  itemId: string,
  params: undefined | GetPlaidItemTransactionsParams,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getPlaidItemTransactions>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getPlaidItemTransactions>>,
          TError,
          Awaited<ReturnType<typeof getPlaidItemTransactions>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetPlaidItemTransactions<
  TData = Awaited<ReturnType<typeof getPlaidItemTransactions>>,
  TError = Error,
>(
  itemId: string,
  params?: GetPlaidItemTransactionsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getPlaidItemTransactions>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getPlaidItemTransactions>>,
          TError,
          Awaited<ReturnType<typeof getPlaidItemTransactions>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetPlaidItemTransactions<
  TData = Awaited<ReturnType<typeof getPlaidItemTransactions>>,
  TError = Error,
>(
  itemId: string,
  params?: GetPlaidItemTransactionsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getPlaidItemTransactions>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List staged transactions for an item
 */

export function useGetPlaidItemTransactions<
  TData = Awaited<ReturnType<typeof getPlaidItemTransactions>>,
  TError = Error,
>(
  itemId: string,
  params?: GetPlaidItemTransactionsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getPlaidItemTransactions>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetPlaidItemTransactionsQueryOptions(itemId, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Creates (or updates) a Fintecy account per selected staged account and imports its staged transactions. Idempotent — re-running only adds what is not already linked.
 * @summary Import staged Plaid accounts into Fintecy
 */
export const importPlaidAccounts = (
  itemId: string,
  plaidImportRequest: PlaidImportRequest,
  signal?: AbortSignal
) => {
  return customInstance<PlaidImportResult>({
    url: `/api/integrations/plaid/items/${itemId}/import`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: plaidImportRequest,
    signal,
  });
};

export const getImportPlaidAccountsMutationOptions = <
  TError = Error,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof importPlaidAccounts>>,
    TError,
    { itemId: string; data: PlaidImportRequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof importPlaidAccounts>>,
  TError,
  { itemId: string; data: PlaidImportRequest },
  TContext
> => {
  const mutationKey = ['importPlaidAccounts'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof importPlaidAccounts>>,
    { itemId: string; data: PlaidImportRequest }
  > = (props) => {
    const { itemId, data } = props ?? {};

    return importPlaidAccounts(itemId, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type ImportPlaidAccountsMutationResult = NonNullable<
  Awaited<ReturnType<typeof importPlaidAccounts>>
>;
export type ImportPlaidAccountsMutationBody = PlaidImportRequest;
export type ImportPlaidAccountsMutationError = Error;

/**
 * @summary Import staged Plaid accounts into Fintecy
 */
export const useImportPlaidAccounts = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof importPlaidAccounts>>,
      TError,
      { itemId: string; data: PlaidImportRequest },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof importPlaidAccounts>>,
  TError,
  { itemId: string; data: PlaidImportRequest },
  TContext
> => {
  const mutationOptions = getImportPlaidAccountsMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
